import React, { useState } from "react";
import axios from "axios";
import { BsExclamationTriangleFill, BsXLg } from "react-icons/bs";
import API_BASE_URL from "../../config";
import "./CancelSubscriptionModal.css";

const REASONS = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "not_using", label: "Not using it enough" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "switching_provider", label: "Switching to another provider" },
  { value: "temporary", label: "Temporary pause" },
  { value: "other", label: "Other" },
];

const formatDate = (value) => {
  if (!value) return "the end of your current billing period";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "the end of your current billing period";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export default function CancelSubscriptionModal({ open, periodEnd, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleClose = () => {
    if (submitting) return;
    setReason("");
    setDetails("");
    setError("");
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!reason) {
      setError("Please select a reason so we can improve.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        throw new Error("Session expired. Please log in again.");
      }
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.post(
        `${API_BASE_URL}/billing/cancel-subscription`,
        { reason, details: details.trim() || undefined },
        { headers }
      );
      onCancelled?.(data);
      setReason("");
      setDetails("");
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Unable to cancel subscription.");
    } finally {
      setSubmitting(false);
    }
  };

  const niceDate = formatDate(periodEnd);
  const charsLeft = 500 - details.length;

  return (
    <div className="cancel-sub-modal" role="dialog" aria-modal="true">
      <div className="cancel-sub-modal__content">
        <div className="cancel-sub-modal__header">
          <h6>We're sorry to see you go</h6>
          <button
            type="button"
            className="cancel-sub-modal__close"
            onClick={handleClose}
            aria-label="Close"
            disabled={submitting}
          >
            <BsXLg />
          </button>
        </div>

        <div className="cancel-sub-modal__body">
          <div className="cancel-sub-warning">
            <BsExclamationTriangleFill className="cancel-sub-warning__icon" />
            <div>
              <p className="cancel-sub-warning__title">
                Your access stays active until <strong>{niceDate}</strong>.
              </p>
              <p className="cancel-sub-warning__text">
                After that date, SMS sending and receiving will stop. Your
                account, contacts, and chat history stay accessible — you can
                reactivate anytime.
              </p>
            </div>
          </div>

          <div className="cancel-sub-field">
            <label htmlFor="cancel-reason">
              Why are you cancelling? <span className="required">*</span>
            </label>
            <select
              id="cancel-reason"
              className="cancel-sub-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
            >
              <option value="">Select a reason...</option>
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div className="cancel-sub-field">
            <label htmlFor="cancel-details">Anything else you'd like to share? <span className="optional">(optional)</span></label>
            <textarea
              id="cancel-details"
              className="cancel-sub-textarea"
              maxLength={500}
              rows={4}
              placeholder="Your feedback helps us build a better product..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              disabled={submitting}
            />
            <div className="cancel-sub-charcount">{charsLeft} characters left</div>
          </div>

          {error && <div className="cancel-sub-error">{error}</div>}
        </div>

        <div className="cancel-sub-modal__actions">
          <button
            type="button"
            className="cancel-sub-btn cancel-sub-btn--ghost"
            onClick={handleClose}
            disabled={submitting}
          >
            Keep Subscription
          </button>
          <button
            type="button"
            className="cancel-sub-btn cancel-sub-btn--danger"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Cancelling..." : "Cancel Subscription"}
          </button>
        </div>
      </div>
    </div>
  );
}
